import React, { useState } from 'react';
import { Menu, X, Search, Home, Trophy, Users, Mail, Share2, UserPlus } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { categories } from '@/data/articles';
import { useScrollPosition } from '@/hooks/useScrollPosition';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

const Navbar: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { isScrolled } = useScrollPosition(10);
  const navigate = useNavigate();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    if (isSearchOpen) setIsSearchOpen(false);
  };

  const toggleSearch = () => {
    setIsSearchOpen(!isSearchOpen);
    if (isMenuOpen) setIsMenuOpen(false);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setIsSearchOpen(false);
      setSearchQuery('');
    }
  };

  const navLinks = [
    { name: 'Home', icon: <Home className="h-3.5 w-3.5" />, href: '/' },
    { name: 'Success Story', icon: <Trophy className="h-3.5 w-3.5" />, href: '/success-story' },
    { name: 'Achievers', icon: <Users className="h-3.5 w-3.5" />, href: '/achievers' },
    { name: 'Contact Us', icon: <Mail className="h-3.5 w-3.5" />, href: '/contact' },
  ];

  return (
    <header 
      className={cn(
        "w-full transition-all duration-300 ease-in-out z-50 sticky top-0",
        isScrolled ? "bg-background/80 backdrop-blur-md border-b border-border/50 shadow-sm" : "py-2"
      )}
    >
      <div className="container mx-auto px-3 md:px-4">
        <div className="flex items-center justify-between h-14">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center justify-start">
            <Link to="/" className="flex items-center">
              <div className="h-10 w-auto mr-2 logo-hover">
                <img 
                  src="/lovable-uploads/2a9179c4-ebae-4500-a868-8f17494b0526.png" 
                  alt="Manamu Media" 
                  className="h-full w-auto" 
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = '/placeholder.svg';
                  }}
                />
              </div>
              <div className="flex flex-col">
                <span className="text-xl font-bold tracking-tight">Manamu Media</span>
                <span className="text-xs font-medium -mt-1 text-foreground">#No.1 Entrepreneur Media Platform</span>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation - Centered */}
          <nav className="hidden md:flex items-center justify-center space-x-0.5 lg:space-x-2 flex-grow-0">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className="px-1.5 py-1.5 rounded-md text-xs font-medium text-foreground/80 hover:text-foreground hover:bg-accent transition-colors duration-200 flex items-center whitespace-nowrap"
              >
                {link.icon}
                <span className="ml-1">{link.name}</span>
              </Link>
            ))}
          </nav>

          {/* CTA Buttons and Search - Right Side */}
          <div className="flex items-center justify-end space-x-1 lg:space-x-2">
            {/* CTA Buttons - Desktop */}
            <div className="hidden md:flex items-center space-x-1.5">
              <Button 
                variant="outline" 
                size="sm" 
                className="flex items-center text-xs h-8 px-2"
                onClick={() => window.open("https://wa.me/+919347537540?text=Hi%2C%20I%20want%20to%20list%20my%20story%20on%20Manamu%20Media", "_blank")}
              >
                <Share2 className="h-3.5 w-3.5 mr-1" />
                <span className="whitespace-nowrap">Share Story</span>
              </Button>
              <Button 
                variant="default" 
                size="sm" 
                className="flex items-center text-xs h-8 px-2"
                onClick={() => window.open("https://www.whatsapp.com/channel/0029Vak8HQZGU3BRAbCQQg3e", "_blank")}
              >
                <UserPlus className="h-3.5 w-3.5 mr-1" />
                <span className="whitespace-nowrap">Join Us</span>
              </Button>
            </div>

            {/* Search Button */}
            <button
              onClick={toggleSearch}
              className="p-1.5 rounded-full text-foreground/80 hover:text-foreground hover:bg-accent transition-colors duration-200"
              aria-label="Search"
            >
              <Search className="h-4 w-4" />
            </button>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <button
                onClick={toggleMenu}
                className="p-1.5 rounded-full text-foreground/80 hover:text-foreground hover:bg-accent transition-colors duration-200"
                aria-label="Menu"
              >
                {isMenuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        {isSearchOpen && (
          <div className="py-3 px-4 md:px-0 animate-fade-in">
            <form onSubmit={handleSearch} className="relative">
              <input
                type="text"
                placeholder="Search articles..."
                className="w-full px-4 py-2 pl-9 rounded-lg border border-border bg-background/50 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                autoFocus
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <button 
                type="submit"
                className="absolute right-3 top-2 px-2 py-1 rounded bg-primary text-white text-xs font-medium"
                disabled={!searchQuery.trim()}
              >
                Search
              </button>
            </form>
          </div>
        )}

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden py-3 animate-fade-in">
            <nav className="flex flex-col space-y-1 px-4 md:px-0">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className="px-3 py-2 rounded-md text-sm font-medium text-foreground/80 hover:text-foreground hover:bg-accent transition-colors duration-200 flex items-center"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.icon}
                  <span className="ml-1.5">{link.name}</span>
                </Link>
              ))}
              
              {/* Mobile CTA Buttons */}
              <div className="flex flex-col space-y-2 mt-3 pt-3 border-t border-border/50">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="justify-start"
                  onClick={() => window.open("https://wa.me/+919347537540?text=Hi%2C%20I%20want%20to%20list%20my%20story%20on%20Manamu%20Media", "_blank")}
                >
                  <Share2 className="h-4 w-4 mr-1.5" />
                  Share Your Story
                </Button>
                <Button 
                  variant="default" 
                  size="sm" 
                  className="justify-start"
                  onClick={() => window.open("https://www.whatsapp.com/channel/0029Vak8HQZGU3BRAbCQQg3e", "_blank")}
                >
                  <UserPlus className="h-4 w-4 mr-1.5" />
                  Join Community
                </Button>
              </div>
              
              <div className="mt-3 pt-3 border-t border-border/50">
                <h3 className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Categories
                </h3>
                {categories.map((category) => (
                  <a
                    key={category.id}
                    href={`#${category.id}`}
                    className="px-3 py-2 rounded-md text-sm font-medium text-foreground/80 hover:text-foreground hover:bg-accent transition-colors duration-200 flex items-center"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {category.name}
                  </a>
                ))}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
